import * as R from "ramda";
import * as React from "react";
import { ConnectDropTarget, DropTarget, DropTargetMonitor } from "react-dnd";

import {
  CourseInstructionType,
  CourseUnitView,
  ICourseComponent,
  ICourseEditOperations,
  isInstruction,
} from "../../../course-author/types";

export interface IDropTargetOwnProps {
  alternateColor: boolean;
  dropPosition: number;
  path: CourseUnitView[];
  type: CourseInstructionType | null;
  editOperations: ICourseEditOperations;
}

interface IDropTargetCollectedProps {
  display: boolean;
  connectDropTarget: ConnectDropTarget;
  didDrop: boolean;
  droppedItem: React.Component;
  isOver: boolean;
}

export type IDropTargetProps = IDropTargetOwnProps & IDropTargetCollectedProps;

export interface IDropItem {
  item: ICourseComponent;
  dropPosition: number;
  path: CourseUnitView[];
  type: CourseInstructionType | null;
}

export const dropTargetSpec = {
  drop(props: IDropTargetProps, monitor: DropTargetMonitor) {
    const { item, dropPosition, path, type } = monitor.getItem() as IDropItem;
    const noOpDrop =
      props.path === path &&
      props.type === type &&
      (dropPosition === props.dropPosition || dropPosition + 1 === props.dropPosition);

    const shouldReact = monitor.canDrop() && !R.isNil(item) && !noOpDrop;
    const typeChanged = type !== props.type;
    const pathChanged = props.path !== path;

    if (!shouldReact) {
      return;
    }

    if (isInstruction(item) && !R.isNil(props.type)) {
      if (pathChanged) {
        return props.editOperations.moveOrCopyComponent(
          props.path,
          R.assocPath(["content", "type"], props.type, item),
          props.dropPosition
        );
      }

      if (typeChanged) {
        props.editOperations.changeInstructionType(path, item, props.type);
      }

      return props.editOperations.moveOrCopyComponent(props.path, item, props.dropPosition);
    }

    if (!isInstruction(item)) {
      return props.editOperations.moveOrCopyComponent(props.path, item, props.dropPosition);
    }
  },
};

const DropTargetFactory: (styleFn: StyleFn) => React.SFC<IDropTargetProps> = styleFn => props =>
  props.connectDropTarget(<div style={styleFn(props)} />);

type StyleFn = (props: IDropTargetProps) => React.CSSProperties;

export default (type: string, styleFn: StyleFn) =>
  DropTarget<IDropTargetOwnProps, IDropTargetCollectedProps>(type, dropTargetSpec, (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    didDrop: monitor.didDrop(),
    display: monitor.canDrop(),
    droppedItem: monitor.getItem(),
    isOver: monitor.isOver(),
  }))(DropTargetFactory(styleFn));

export const invisibleDropTargetStyle = (props: IDropTargetProps, height: string = "3.5rem"): React.CSSProperties => {
  const style = {
    height: "0rem",
    width: "100%",
  };

  if (props.isOver) {
    return {
      ...style,
      height,
      position: "relative",
      transition: "height 0.05s ease-out",
    };
  }

  if (props.display) {
    return {
      ...style,
      height: `calc(${height} - 2rem`,
      position: "absolute",
      transition: "height 0.1s ease-out",
      zIndex: 1,
    };
  }

  return style;
};
